import { defineStore } from "pinia";
import { ref, reactive, computed } from "vue";
import employeeService from "@/services/hrm/employeeService";
import { toast } from "@/utils/toast";
import { createScopedCache } from "@/stores/_cache";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

const employeeListCache = createScopedCache("employees:list");
const employeeDetailCache = createScopedCache("employees:detail");
const employeeReportListCache = createScopedCache("employees:report-list");
const employeeReportSummaryCache = createScopedCache("employees:report-summary");

const EMPLOYEE_LIST_TTL = 2 * 60 * 1000;
const EMPLOYEE_DETAIL_TTL = 5 * 60 * 1000;
const EMPLOYEE_REPORT_TTL = 90 * 1000;

export const useEmployeeStore = defineStore("employee", () => {
  const employees = ref([]);
  const employee = ref(null);
  const loading = ref(false);
  const error = ref(null);

  const items = ref([]);
  const meta = ref({ total: 0, per_page: 25, current_page: 1 });
  const filters = reactive({
    status: "",
    designation_id: "",
    sort: "recent",
    date_from: "",
    date_to: "",
    page: 1,
    per_page: 25,
  });

  const reportLoading = ref(false);
  const reportSummary = ref({
    total_employees: 0,
    active_employees: 0,
    inactive_employees: 0,
    new_employees: 0,
  });

  const employeeChart = ref({
    labels: ["Active", "Inactive"],
    series: [0, 0],
  });

  const hasItems = computed(() => items.value.length > 0);

  function normalizeEmployee(raw) {
    return raw || {};
  }

  function hydrateEmployees(payload) {
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    employees.value = rows.map(normalizeEmployee);
    if (payload?.meta) {
      meta.value = {
        total: Number(payload.meta.total ?? rows.length) || 0,
        per_page: Number(payload.meta.per_page ?? 25) || 25,
        current_page: Number(payload.meta.current_page ?? 1) || 1,
      };
    }
  }

  function hydrateEmployee(payload) {
    employee.value = payload?.row ? normalizeEmployee(payload.row) : null;
  }

  function hydrateReportList(payload) {
    items.value = Array.isArray(payload?.items) ? payload.items : [];
    meta.value = payload?.meta || { total: 0, per_page: 25, current_page: 1 };
    filters.page = meta.value.current_page;
    filters.per_page = meta.value.per_page;
  }

  function hydrateReportSummary(payload) {
    reportSummary.value = payload?.summary || {
      total_employees: 0,
      active_employees: 0,
      inactive_employees: 0,
      new_employees: 0,
    };

    employeeChart.value = payload?.chart || {
      labels: ["Active", "Inactive"],
      series: [0, 0],
    };
  }

  async function fetchEmployees(params = {}, options = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_EMPLOYEE))) {
      employees.value = [];
      items.value = [];
      meta.value = { total: 0, per_page: 25, current_page: 1 };
      return { rows: [] };
    }

    loading.value = true;
    error.value = null;

    try {
      return await employeeListCache.get(params, {
        ttlMs: EMPLOYEE_LIST_TTL,
        force: options.force === true,
        loader: async () => {
          const res = await employeeService.getAll(params);
          const body = res?.data;
          const rows = Array.isArray(body?.data) ? body.data : body;
          return {
            rows: Array.isArray(rows) ? rows : [],
            meta: body?.meta || {
              total: Array.isArray(rows) ? rows.length : 0,
              per_page: params.per_page ?? 25,
              current_page: params.page ?? 1,
            },
          };
        },
        onHydrate: (payload) => {
          hydrateEmployees(payload);
        },
      });
    } catch (err) {
      error.value = err?.response?.data?.message || "Failed to fetch employees";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function ensureEmployees(params = {}, options = {}) {
    return fetchEmployees(params, options);
  }

  async function fetchEmployee(id, options = {}) {
    loading.value = true;
    error.value = null;
    try {
      await employeeDetailCache.get(
        { id },
        {
          ttlMs: EMPLOYEE_DETAIL_TTL,
          force: options.force === true,
          loader: async () => {
            const res = await employeeService.get(id);
            return { row: res.data };
          },
          onHydrate: (payload) => {
            hydrateEmployee(payload);
          },
        },
      );
      return employee.value;
    } catch (err) {
      error.value = err?.response?.data?.message || "Failed to fetch employee";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function ensureEmployee(id, options = {}) {
    return fetchEmployee(id, options);
  }

  function invalidateEmployeeCaches() {
    employeeListCache.invalidate();
    employeeDetailCache.invalidate();
    employeeReportListCache.invalidate();
    employeeReportSummaryCache.invalidate();
  }

  function notifyWelcomeEmailWarning(payload) {
    const warning = payload?.welcome_email_warning;
    if (warning) {
      toast(warning, "warning");
    }
  }

  async function createEmployee(data, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      const res = await employeeService.create(data, companyId);
      invalidateEmployeeCaches();
      toast("Employee created successfully");
      notifyWelcomeEmailWarning(res.data);
      await fetchEmployees({}, { force: true });
      return res.data;
    } catch (err) {
      error.value = err?.response?.data?.message || "Failed to create employee";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateEmployee(id, data, companyId = null) {
    loading.value = true;
    error.value = null;
    try {
      const res = await employeeService.update(id, data, companyId);
      invalidateEmployeeCaches();
      toast("Update successfull");
      notifyWelcomeEmailWarning(res.data);
      await fetchEmployees({}, { force: true });
      return res.data;
    } catch (err) {
      error.value = err?.response?.data?.message || "Failed to update employee";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteEmployee(id) {
    loading.value = true;
    error.value = null;
    try {
      await employeeService.delete(id);
      invalidateEmployeeCaches();
      toast("Employee deleted successfully");
      await fetchEmployees({}, { force: true });
    } catch (err) {
      error.value = err?.response?.data?.message || "Failed to delete employee";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true;
    error.value = null;
    try {
      await employeeService.bulkDelete(ids);
      invalidateEmployeeCaches();
      toast("Employees deleted successfully");
      await fetchEmployees({}, { force: true });
    } catch (err) {
      error.value = err?.response?.data?.message || "Failed to bulk delete";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function bulkSetStatus(ids, status) {
    loading.value = true;
    error.value = null;
    try {
      const res = await employeeService.bulkSetStatus(ids, status);
      invalidateEmployeeCaches();
      toast(res.data?.message || "Employee status updated.");
      await fetchEmployees({}, { force: true });
      return res.data;
    } catch (err) {
      error.value =
        err?.response?.data?.message || "Failed to update employee status";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function assignPermissions(id, permissions) {
    loading.value = true;
    error.value = null;
    try {
      await employeeService.assignPermissions(id, permissions);
      employeeDetailCache.invalidate({ id });
      toast("Permissions assigned successfully");
    } catch (err) {
      error.value =
        err?.response?.data?.message || "Failed to assign permissions";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function list(extra = {}, options = {}) {
    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters, ...extra };
      return await employeeReportListCache.get(params, {
        ttlMs: EMPLOYEE_REPORT_TTL,
        force: options.force === true,
        loader: async () => {
          const { data } = await employeeService.reportList(params);
          return {
            items: data.data || data.items || [],
            meta: {
              total: data.meta?.total ?? (data.data || data.items || []).length,
              per_page: data.meta?.per_page ?? params.per_page ?? 25,
              current_page: data.meta?.current_page ?? params.page ?? 1,
            },
          };
        },
        onHydrate: (payload) => {
          hydrateReportList(payload);
        },
      });
    } catch (e) {
      error.value =
        e?.response?.data?.message ||
        e.message ||
        "Failed to load employees report";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureList(extra = {}, options = {}) {
    return list(extra, options);
  }

  async function fetchReport(extra = {}, options = {}) {
    reportLoading.value = true;
    try {
      const params = { ...filters, ...extra };
      return await employeeReportSummaryCache.get(params, {
        ttlMs: EMPLOYEE_REPORT_TTL,
        force: options.force === true,
        loader: async () => {
          const { data } = await employeeService.report(params);
          return {
            summary: {
              total_employees: data?.summary?.total_employees ?? 0,
              active_employees: data?.summary?.active_employees ?? 0,
              inactive_employees: data?.summary?.inactive_employees ?? 0,
              new_employees: data?.summary?.new_employees ?? 0,
            },
            chart: {
              labels: data?.chart?.labels || ["Active", "Inactive"],
              series: data?.chart?.series || [0, 0],
            },
          };
        },
        onHydrate: (payload) => {
          hydrateReportSummary(payload);
        },
      });
    } catch (e) {
      const msg =
        e?.response?.data?.message || e.message || "Failed to load report";
      toast(msg, "error");
      throw e;
    } finally {
      reportLoading.value = false;
    }
  }

  async function ensureReport(extra = {}, options = {}) {
    return fetchReport(extra, options);
  }

  async function downloadReport(format = "xlsx", extra = {}) {
    try {
      const params = { ...filters, ...extra };
      const res = await employeeService.exportReport(format, params);

      const mime =
        format === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const ext = format === "pdf" ? "pdf" : "xlsx";

      link.href = url;
      link.setAttribute(
        "download",
        `employee-report-${new Date().toISOString().slice(0, 10)}.${ext}`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg =
        e?.response?.data?.message || e.message || "Failed to export report";
      toast(msg, "error");
      throw e;
    }
  }

  async function fetchOrgTree(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_EMPLOYEE))) {
      return { data: [], meta: { roots: 0 } };
    }
    loading.value = true;
    error.value = null;
    try {
      const res = await employeeService.orgTree(params);
      return res.data;
    } catch (err) {
      error.value = err?.response?.data?.message || "Failed to load org chart";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    employees,
    employee,
    loading,
    error,
    fetchEmployees,
    ensureEmployees,
    fetchEmployee,
    ensureEmployee,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    bulkDelete,
    bulkSetStatus,
    assignPermissions,
    invalidateEmployeeCaches,
    fetchOrgTree,

    items,
    meta,
    filters,
    reportLoading,
    reportSummary,
    employeeChart,
    hasItems,
    list,
    ensureList,
    fetchReport,
    ensureReport,
    downloadReport,
  };
});
